/**
 * Produkt-News für Nutzer (PV-037): Abfrage, neu/Archiv, Zählmarke. Die Entscheidungen sind reine
 * Funktionen (news.test.ts); `useNews` teilen sich die Zählmarke in der Kopfleiste und die
 * Hilfeseite (eine Abfrage, ein Cache).
 */
import type { NewsSprache, ProduktNews } from '@projektverwaltung/shared';
import { useQuery } from '@tanstack/react-query';
import { createContext } from 'react';
import { api } from './api.js';
import { useSession } from './session.js';

export const NEWS_QUERY_KEY = ['news'] as const;

/**
 * Stand der Hilfeseite für die News (A12). `null` = kein Besuch (z. B. ⓘ-Popover). `wartet` = die
 * Seite hat noch keine für diesen Besuch frische Antwort — der Bereich zeigt den Ladezustand statt
 * einer Aufteilung aus altem Cache. `aktiv` = „gelesen bis", eingefroren beim Öffnen: die in
 * diesem Besuch neuen News bleiben oben, obwohl der Server „gelesen" schon bestätigt hat.
 */
export type NewsBesuch = { status: 'wartet' } | { status: 'aktiv'; gelesenBis: string };
export const NewsBesuchContext = createContext<NewsBesuch | null>(null);

type MitVeroeffentlichung = Pick<ProduktNews, 'veroeffentlicht_am'>;

/** Neu = veröffentlicht NACH „gelesen bis" (Änderungen an einer News verschieben das nicht, A11). */
export function istNeu(news: MitVeroeffentlichung, gelesenBis: string): boolean {
  return (
    news.veroeffentlicht_am !== null && Date.parse(news.veroeffentlicht_am) > Date.parse(gelesenBis)
  );
}

export function ungelesenAnzahl(
  items: readonly MitVeroeffentlichung[],
  gelesenBis: string,
): number {
  return items.filter((n) => istNeu(n, gelesenBis)).length;
}

export function teileNeuArchiv<T extends MitVeroeffentlichung>(
  items: readonly T[],
  gelesenBis: string,
): { neu: T[]; archiv: T[] } {
  const neu: T[] = [];
  const archiv: T[] = [];
  for (const n of items) {
    if (istNeu(n, gelesenBis)) neu.push(n);
    else archiv.push(n);
  }
  return { neu, archiv };
}

/** Text der Zählmarke: 0 → keine Marke, ab 10 „9+". */
export function zaehlmarkeText(anzahl: number): string | null {
  if (anzahl <= 0) return null;
  return anzahl > 9 ? '9+' : String(anzahl);
}

export function newsInSprache(
  news: Pick<ProduktNews, 'titel_de' | 'titel_en' | 'text_de' | 'text_en'>,
  sprache: NewsSprache,
): { titel: string; text: string } {
  return sprache === 'en'
    ? { titel: news.titel_en, text: news.text_en }
    : { titel: news.titel_de, text: news.text_de };
}

/**
 * Eine Abfrage, ein Cache (`NEWS_QUERY_KEY`). Die Hilfeseite verlangt mit `frischBeimOeffnen` bei
 * jedem Öffnen eine neue Antwort: Sie friert „gelesen bis" ein und meldet „gelesen" — auf einem
 * bis zu fünf Minuten alten Cache der Kopfleiste würde eine inzwischen veröffentlichte News nie
 * als neu gezeigt, aber als gelesen markiert.
 */
export function useNews({ frischBeimOeffnen = false }: { frischBeimOeffnen?: boolean } = {}) {
  const { user } = useSession();
  return useQuery({
    queryKey: NEWS_QUERY_KEY,
    queryFn: () => api.listNews(),
    enabled: !!user,
    staleTime: 5 * 60_000,
    refetchOnMount: frischBeimOeffnen ? 'always' : true,
  });
}
